// E2E envelope: types, AAD construction, canonical serialization.
//
// The envelope is the wire format that sits in `messages_e2e.envelope`.
// The API_Server stores it as opaque bytes — it must contain enough
// public material for every intended recipient to recover the
// plaintext, and nothing more. Specifically, the design (`design.md`,
// "Crypto_Layer / E2E Envelope") nails it down to:
//
//     { v: 1,
//       alg: "ecdh-p256+hkdf+aes-256-gcm",
//       senderEphPub:    65 bytes (uncompressed SEC1 P-256 point),
//       recipients: [
//         { accountIdHash: 32 bytes,
//           wrappedKey:    AES-256-GCM ciphertext + tag of MK_msg,
//           iv:            12 bytes (per-recipient wrap IV) },
//         ...
//       ],
//       ct:  AES-256-GCM ciphertext + tag over plaintext (under MK_msg),
//       iv:  12 bytes (content IV),
//       aad: ASCII("link.e2e.v1") || channel_id || message_id }
//
// ---------------------------------------------------------------------------
// Serialization choice
// ---------------------------------------------------------------------------
//
// `design.md` describes the envelope as "canonical CBOR-ish JSON". We
// pick **canonical JSON with unpadded base64url byte fields** for v1:
//
//   * It is round-trippable bit-for-bit. `serializeE2eEnvelope` always
//     emits the same byte sequence for the same logical envelope —
//     keys at every level are written in a fixed lexicographic-ish
//     order (see `EMIT_*_ORDER` below), recipient list order is
//     preserved as input, no whitespace is inserted, no JSON escapes
//     beyond the necessary set are used.
//   * It is human-debuggable in a pinch (you can `JSON.parse` the
//     bytes to inspect an envelope), which has surprising operational
//     value when triaging "this DM isn't decrypting" reports.
//   * It avoids pulling a CBOR encoder into the browser bundle for v1.
//
// The format is versioned by the `v` field. If we later move to CBOR,
// we bump `v` and add a parallel decoder; old envelopes keep working.
//
// Base64url (RFC 4648 §5) without padding is the byte-field encoding.
// Unpadded keeps the wire form compact and avoids the `=` parser
// quirks some JSON consumers have tripped on; we never need length
// modulo 4 to round-trip because we always know each field's expected
// length (or read it from the decoded bytes themselves).
//
// _Requirements: 8.2, 11.5, 15.3, 15.4_

import {
  CONTENT_IV_BYTES,
  E2E_ALG,
  E2E_AAD_PREFIX,
  E2E_VERSION,
  ENVELOPE_KEY_ORDER,
  RECIPIENT_KEY_ORDER,
  SEC1_UNCOMPRESSED_LENGTH,
  WRAP_IV_BYTES,
} from "./internals";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * One recipient input to {@link encryptE2e}. The `accountIdHash` is the
 * canonical 32-byte SHA-256 of the recipient's `publicKeyRaw` (matching
 * `accounts.account_id_hash` in the schema). The `identityPublicKey`
 * is the 65-byte uncompressed SEC1 P-256 point that ECDH runs against.
 *
 * The two fields are kept separate (rather than re-deriving one from
 * the other inside `encryptE2e`) so that callers can use the hash they
 * already have on hand from the API response, without an extra
 * SHA-256 per recipient.
 */
export interface E2eRecipient {
  /** SHA-256(publicKeyRaw); 32 bytes. */
  accountIdHash: Uint8Array;
  /** 65-byte uncompressed SEC1 P-256 public key. */
  identityPublicKey: Uint8Array;
}

/**
 * One recipient slot inside an {@link E2eEnvelope}. Decoded shape only
 * — when serialized to wire form, the byte fields become unpadded
 * base64url strings.
 */
export interface E2eEnvelopeRecipient {
  /** SHA-256(recipient publicKeyRaw); 32 bytes. */
  accountIdHash: Uint8Array;
  /**
   * AES-256-GCM ciphertext + 16-byte tag of the per-message key
   * (`MK_msg`) under the per-recipient HKDF-derived wrap key. Length
   * is exactly `MK_BYTES + GCM_TAG_BYTES = 48`.
   */
  wrappedKey: Uint8Array;
  /** 12-byte AES-GCM nonce used for the wrap of this recipient's slot. */
  iv: Uint8Array;
}

/**
 * The decoded E2E envelope. {@link encryptE2e} returns one of these,
 * {@link decryptE2e} consumes one. Use {@link serializeE2eEnvelope}
 * and {@link parseE2eEnvelope} to convert to/from wire bytes.
 *
 * All byte fields are owned by the envelope — callers MAY mutate them
 * after construction at their own risk; the library does not retain
 * references after the function call returns.
 */
export interface E2eEnvelope {
  /** Envelope schema version. v1 is the only value currently emitted. */
  v: typeof E2E_VERSION;
  /** Algorithm tag. v1 is `ecdh-p256+hkdf+aes-256-gcm`. */
  alg: typeof E2E_ALG;
  /**
   * Sender's per-message ephemeral public key, 65-byte uncompressed
   * SEC1. The matching private scalar is generated fresh per call to
   * {@link encryptE2e} and discarded immediately.
   */
  senderEphPub: Uint8Array;
  /**
   * Per-recipient slots. Order is preserved across serialize/parse
   * round trips. {@link decryptE2e} matches by `accountIdHash`.
   */
  recipients: E2eEnvelopeRecipient[];
  /** AES-256-GCM ciphertext + 16-byte tag over the plaintext. */
  ct: Uint8Array;
  /** 12-byte content IV used for `ct`. */
  iv: Uint8Array;
  /**
   * Additional Authenticated Data bound into the content AES-GCM call.
   * v1 layout: `ASCII("link.e2e.v1") || channel_id || message_id`,
   * with `channel_id` and `message_id` UTF-8-encoded.
   */
  aad: Uint8Array;
}

// ---------------------------------------------------------------------------
// AAD construction
// ---------------------------------------------------------------------------

const TEXT_ENCODER = new TextEncoder();

/**
 * Build the canonical Additional Authenticated Data for an E2E
 * message. The AAD binds the content ciphertext to a specific
 * `(channel, message)` pair so a copied ciphertext cannot be replayed
 * into a different conversation context without producing an AES-GCM
 * tag mismatch.
 *
 * Layout: `ASCII("link.e2e.v1") || utf8(channelId) || utf8(messageId)`.
 *
 * No length prefixes are inserted between fields. The two ID fields
 * are application-controlled XID strings that never contain bytes from
 * the constant prefix, and a single-purpose use of this AAD inside
 * `encryptE2e`/`decryptE2e` only ever compares two AADs built the same
 * way — so length-prefixing would add complexity without security
 * benefit.
 *
 * Exported because external callers (Secrets layer in task 3.9, file
 * encryption in task 3.11) reuse the same construction or its
 * derivatives.
 */
export function buildE2eAad(
  channelId: string,
  messageId: string,
): Uint8Array {
  const channelBytes = TEXT_ENCODER.encode(channelId);
  const messageBytes = TEXT_ENCODER.encode(messageId);
  const out = new Uint8Array(
    E2E_AAD_PREFIX.length + channelBytes.length + messageBytes.length,
  );
  out.set(E2E_AAD_PREFIX, 0);
  out.set(channelBytes, E2E_AAD_PREFIX.length);
  out.set(messageBytes, E2E_AAD_PREFIX.length + channelBytes.length);
  return out;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Discriminated kinds of envelope-level failure. These are surfaced by
 * {@link parseE2eEnvelope} (and re-used by `encryptE2e`/`decryptE2e`
 * for input validation).
 *
 * - `bad_json`             : input bytes are not valid JSON.
 * - `bad_shape`            : JSON parsed but did not match the
 *                             envelope schema.
 * - `bad_version`          : `v` is present but not 1.
 * - `bad_alg`              : `alg` is present but not the v1 alg tag.
 * - `bad_base64`           : a byte field could not be base64url-decoded.
 * - `bad_field_length`     : a fixed-length byte field had the wrong size.
 * - `empty_recipients`     : `recipients` was an empty array.
 * - `not_recipient`        : caller's account-id-hash is not in the
 *                             envelope (used by `decryptE2e`).
 * - `decrypt_failure`      : AES-GCM tag mismatch on wrap or content
 *                             (used by `decryptE2e`).
 */
export type E2eErrorKind =
  | "bad_json"
  | "bad_shape"
  | "bad_version"
  | "bad_alg"
  | "bad_base64"
  | "bad_field_length"
  | "empty_recipients"
  | "not_recipient"
  | "decrypt_failure";

export class E2eError extends Error {
  constructor(
    public readonly kind: E2eErrorKind,
    message: string,
    cause?: unknown,
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "E2eError";
  }
}

// ---------------------------------------------------------------------------
// Canonical serialization
// ---------------------------------------------------------------------------

/**
 * Serialize an {@link E2eEnvelope} to its canonical wire bytes (UTF-8
 * JSON with unpadded base64url byte fields).
 *
 * The output is deterministic: the same logical envelope always yields
 * the same byte sequence. Top-level keys are emitted in
 * {@link ENVELOPE_KEY_ORDER}, per-recipient keys in
 * {@link RECIPIENT_KEY_ORDER}, recipient array order is preserved as
 * given. No whitespace is inserted.
 *
 * @throws {@link E2eError} (`bad_field_length`) if any required-fixed
 *   byte field has the wrong length. Catches programmer bugs (we don't
 *   want to ship envelopes that won't round-trip).
 */
export function serializeE2eEnvelope(env: E2eEnvelope): Uint8Array {
  if (env.v !== E2E_VERSION) {
    throw new E2eError("bad_version", `unsupported envelope version: ${env.v}`);
  }
  if (env.alg !== E2E_ALG) {
    throw new E2eError("bad_alg", `unsupported envelope alg: ${env.alg}`);
  }
  assertLength(
    "senderEphPub",
    env.senderEphPub,
    SEC1_UNCOMPRESSED_LENGTH,
  );
  assertLength("iv", env.iv, CONTENT_IV_BYTES);
  if (env.recipients.length === 0) {
    throw new E2eError(
      "empty_recipients",
      "envelope must have at least one recipient",
    );
  }
  for (let i = 0; i < env.recipients.length; i++) {
    const r = env.recipients[i]!;
    assertLength(`recipients[${i}].accountIdHash`, r.accountIdHash, 32);
    assertLength(`recipients[${i}].iv`, r.iv, WRAP_IV_BYTES);
    if (r.wrappedKey.length === 0) {
      throw new E2eError(
        "bad_field_length",
        `recipients[${i}].wrappedKey is empty`,
      );
    }
  }

  // Hand-rolled JSON emission with fixed key order — JSON.stringify's
  // key order in modern engines is "insertion order", which we control
  // here by assembling a plain string. Keeping this off `JSON.stringify`
  // also lets us guarantee unpadded-base64url byte fields and avoids a
  // dependency on object-property-order being preserved across engines.
  let s = "{";
  for (let i = 0; i < ENVELOPE_KEY_ORDER.length; i++) {
    const key = ENVELOPE_KEY_ORDER[i]!;
    if (i > 0) s += ",";
    s += JSON.stringify(key);
    s += ":";
    s += emitEnvelopeField(env, key);
  }
  s += "}";
  return TEXT_ENCODER.encode(s);
}

/**
 * Parse the canonical wire bytes back into a typed {@link E2eEnvelope}.
 *
 * On any malformed input the function throws an {@link E2eError} with
 * a `kind` that distinguishes "not JSON" from "JSON but wrong shape"
 * from "shape OK but a fixed-length field is the wrong length".
 *
 * The returned byte fields are fresh buffers — mutating them does not
 * affect the input bytes, and vice versa.
 */
export function parseE2eEnvelope(bytes: Uint8Array): E2eEnvelope {
  let raw: unknown;
  try {
    raw = JSON.parse(new TextDecoder().decode(bytes));
  } catch (cause) {
    throw new E2eError(
      "bad_json",
      "envelope bytes are not valid UTF-8 JSON",
      cause,
    );
  }
  if (typeof raw !== "object" || raw === null) {
    throw new E2eError("bad_shape", "envelope JSON root is not an object");
  }
  const obj = raw as Record<string, unknown>;

  if (obj["v"] !== E2E_VERSION) {
    throw new E2eError(
      "bad_version",
      `unsupported envelope version: ${String(obj["v"])}`,
    );
  }
  if (obj["alg"] !== E2E_ALG) {
    throw new E2eError(
      "bad_alg",
      `unsupported envelope alg: ${String(obj["alg"])}`,
    );
  }

  const senderEphPub = decodeFixedField(
    "senderEphPub",
    obj["senderEphPub"],
    SEC1_UNCOMPRESSED_LENGTH,
  );
  const iv = decodeFixedField("iv", obj["iv"], CONTENT_IV_BYTES);
  const ct = decodeVariableField("ct", obj["ct"]);
  const aad = decodeVariableField("aad", obj["aad"]);

  const recipientsRaw = obj["recipients"];
  if (!Array.isArray(recipientsRaw)) {
    throw new E2eError("bad_shape", "envelope.recipients is not an array");
  }
  if (recipientsRaw.length === 0) {
    throw new E2eError(
      "empty_recipients",
      "envelope.recipients must be non-empty",
    );
  }
  const recipients: E2eEnvelopeRecipient[] = new Array(recipientsRaw.length);
  for (let i = 0; i < recipientsRaw.length; i++) {
    const r = recipientsRaw[i];
    if (typeof r !== "object" || r === null) {
      throw new E2eError(
        "bad_shape",
        `envelope.recipients[${i}] is not an object`,
      );
    }
    const ri = r as Record<string, unknown>;
    recipients[i] = {
      accountIdHash: decodeFixedField(
        `recipients[${i}].accountIdHash`,
        ri["accountIdHash"],
        32,
      ),
      wrappedKey: decodeVariableField(
        `recipients[${i}].wrappedKey`,
        ri["wrappedKey"],
      ),
      iv: decodeFixedField(`recipients[${i}].iv`, ri["iv"], WRAP_IV_BYTES),
    };
  }

  return {
    v: E2E_VERSION,
    alg: E2E_ALG,
    senderEphPub,
    recipients,
    ct,
    iv,
    aad,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function emitEnvelopeField(env: E2eEnvelope, key: string): string {
  switch (key) {
    case "v":
      return String(env.v);
    case "alg":
      return JSON.stringify(env.alg);
    case "senderEphPub":
      return JSON.stringify(toBase64Url(env.senderEphPub));
    case "recipients": {
      let s = "[";
      for (let i = 0; i < env.recipients.length; i++) {
        if (i > 0) s += ",";
        s += emitRecipient(env.recipients[i]!);
      }
      s += "]";
      return s;
    }
    case "ct":
      return JSON.stringify(toBase64Url(env.ct));
    case "iv":
      return JSON.stringify(toBase64Url(env.iv));
    case "aad":
      return JSON.stringify(toBase64Url(env.aad));
    default:
      // Unreachable: ENVELOPE_KEY_ORDER is exhaustive for the typed envelope.
      throw new Error(`emitEnvelopeField: unknown key "${key}"`);
  }
}

function emitRecipient(r: E2eEnvelopeRecipient): string {
  let s = "{";
  for (let i = 0; i < RECIPIENT_KEY_ORDER.length; i++) {
    const key = RECIPIENT_KEY_ORDER[i]!;
    if (i > 0) s += ",";
    s += JSON.stringify(key);
    s += ":";
    switch (key) {
      case "accountIdHash":
        s += JSON.stringify(toBase64Url(r.accountIdHash));
        break;
      case "wrappedKey":
        s += JSON.stringify(toBase64Url(r.wrappedKey));
        break;
      case "iv":
        s += JSON.stringify(toBase64Url(r.iv));
        break;
      default:
        throw new Error(`emitRecipient: unknown key "${key}"`);
    }
  }
  s += "}";
  return s;
}

function assertLength(
  name: string,
  value: Uint8Array,
  expected: number,
): void {
  if (value.length !== expected) {
    throw new E2eError(
      "bad_field_length",
      `${name} must be exactly ${expected} bytes, got ${value.length}`,
    );
  }
}

function decodeFixedField(
  name: string,
  value: unknown,
  expected: number,
): Uint8Array {
  const decoded = decodeVariableField(name, value);
  if (decoded.length !== expected) {
    throw new E2eError(
      "bad_field_length",
      `${name} must be exactly ${expected} bytes, got ${decoded.length}`,
    );
  }
  return decoded;
}

function decodeVariableField(name: string, value: unknown): Uint8Array {
  if (typeof value !== "string") {
    throw new E2eError(
      "bad_shape",
      `${name} must be a base64url string, got ${typeof value}`,
    );
  }
  try {
    return fromBase64Url(value);
  } catch (cause) {
    throw new E2eError(
      "bad_base64",
      `${name} is not valid unpadded base64url`,
      cause,
    );
  }
}

// ---------------------------------------------------------------------------
// Base64url (RFC 4648 §5), unpadded
// ---------------------------------------------------------------------------
//
// Implemented in-line because the v1 browser bundle target (Vite SPA)
// does not require this helper anywhere else, and `Buffer` is not
// available in the browser. We pass through `btoa`/`atob`, then swap
// `+/=` for `-_` per RFC 4648 §5.

const B64URL_VALID = /^[A-Za-z0-9_-]*$/;

function toBase64Url(bytes: Uint8Array): string {
  // `btoa` takes a "binary string" (each char's code point in
  // [0,255]). Build it via String.fromCharCode in chunks to avoid
  // call-stack limits for large inputs.
  const CHUNK = 0x8000;
  let bin = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, Math.min(i + CHUNK, bytes.length))),
    );
  }
  const std = btoa(bin);
  // Strip padding, swap to URL-safe alphabet.
  return std.replace(/=+$/u, "").replace(/\+/g, "-").replace(/\//g, "_");
}

function fromBase64Url(s: string): Uint8Array {
  if (!B64URL_VALID.test(s)) {
    throw new Error("input contains invalid base64url characters");
  }
  // Reverse the URL-safe alphabet.
  const std = s.replace(/-/g, "+").replace(/_/g, "/");
  // Re-pad to a multiple of 4. `atob` is strict about length.
  const remainder = std.length % 4;
  const padded =
    remainder === 0
      ? std
      : remainder === 2
        ? std + "=="
        : remainder === 3
          ? std + "="
          : (() => {
              throw new Error(
                "base64url length must be 0, 2, or 3 mod 4 (got 1)",
              );
            })();
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

// Exported solely for unit / property tests in tasks 3.8.
/** @internal */
export const __test_only = {
  toBase64Url,
  fromBase64Url,
};
